import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { Plus } from "lucide-react";
import ScoutInfoDot from "../../components/scout-request/ScoutInfoDot";
import ApplicationRow from "../../components/friendlies/ApplicationRow";
import CloseRequestDialog from "../../components/friendlies/CloseRequestDialog";
import FriendlyComposer from "../../components/friendlies/FriendlyComposer";
import { LocationModePill, OpponentTierLine } from "../../components/friendlies/FriendlyPills";
import { friendliesService } from "../../services/friendliesApi";
import { searchService } from "../../services/api";
import { prefillFromRequest } from "../../components/friendlies/friendlyComposerLogic";

const TABS = [
  { value: "open", label: "Open" },
  { value: "matched", label: "Matched" },
  { value: "expired", label: "Expired" },
  { value: "closed", label: "Closed" },
];

// One open request + its applications (lazy-loaded). Camp multiple-matches stays
// open after each agreement, showing the matches-agreed count.
const OpenRequestCard = ({ request, onClose, onChanged }) => {
  const [apps, setApps] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const navigate = useNavigate();
  const isCampMultiple = request.location?.mode === "camp" && request.camp_multiple_matches;

  const load = useCallback(async () => {
    const res = await friendliesService.getApplications(request.id);
    if (res.success) setApps(res.data);
    setLoaded(true);
  }, [request.id]);
  useEffect(() => { load(); }, [load]);

  const startChat = async (app) => {
    if (app.channel_cid) { navigate(`/inbox?channel=${app.channel_cid}`); return; }
    setBusyId(app.id);
    const res = await friendliesService.startChat(app.id);
    setBusyId(null);
    if (res.success && res.data?.channel_id) navigate(`/inbox?channel=${res.data.channel_id}`);
    else toast.error(res.error || "Could not open the chat.");
  };
  const dismiss = async (app) => {
    setBusyId(app.id);
    const res = await friendliesService.dismissApplication(app.id);
    setBusyId(null);
    if (res.success) setApps((prev) => prev.filter((a) => a.id !== app.id));
    else toast.error(res.error || "Could not dismiss.");
  };
  // Marking a camp match agreed happens IN THE CHAT (friendly_request unfurl CTA),
  // not from this list — the row keeps exactly two actions (M3-review F-M3-3).

  const actionable = apps.filter((a) => a.status !== "dismissed");

  return (
    <div className="bg-white rounded-[12px] border border-[#E6EAF0] p-4">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="text-[14px] font-bold text-[#0F1728]">
            {request.team_category_label}{request.team_label ? ` · ${request.team_label}` : ""} · {request.date?.phrase}
          </div>
          <div className="flex items-center flex-wrap gap-2 mt-1.5">
            <LocationModePill location={request.location} />
            <OpponentTierLine display={request.tier?.display} />
            {isCampMultiple && (
              <span className="inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-bold" style={{ backgroundColor: "#E7F6EF", color: "#0E7B52" }}>
                ✓ {request.matches_agreed} {request.matches_agreed === 1 ? "match agreed" : "matches agreed"}
              </span>
            )}
          </div>
        </div>
        <button onClick={() => onClose(request)} className="px-3 py-1.5 rounded-lg border border-[#E0E6EE] text-[12.5px] font-semibold text-[#33475E] hover:bg-gray-50 flex-shrink-0">Close request</button>
      </div>

      <div className="mt-3">
        <p className="text-[11px] font-bold uppercase tracking-[0.06em] text-[#98A2B3]">
          Applications · {request.application_count ?? actionable.length}
        </p>
        {loaded && actionable.length === 0 ? (
          <p className="mt-2 text-[12.5px] text-[#98A2B3]">No applications yet. Your request is live in the market.</p>
        ) : (
          actionable.map((a) => (
            <ApplicationRow key={a.id} application={a} busy={busyId === a.id}
              onStartChat={startChat} onDismiss={dismiss} />
          ))
        )}
      </div>
    </div>
  );
};

const CalmRequestCard = ({ request, onRepost }) => {
  const repostable = request.status === "expired" || request.status === "closed";
  return (
    <div className="bg-white rounded-[12px] border border-[#E6EAF0] p-4 flex items-center justify-between gap-3">
      <div className="min-w-0">
        <div className="text-[13.5px] font-semibold text-[#33475E]">
          {request.team_category_label}{request.team_label ? ` · ${request.team_label}` : ""} · {request.date?.phrase}
        </div>
        <div className="text-[11.5px] text-[#98A2B3] mt-0.5">
          {request.status === "matched" && "Matched"}
          {request.status === "expired" && "Closed automatically after the match date"}
          {request.status === "closed" && "Closed by you"}
        </div>
      </div>
      {repostable && (
        <button onClick={() => onRepost(request)} className="px-3 py-1.5 rounded-lg border border-[#2283FF] text-[#1866CC] text-[12.5px] font-semibold hover:bg-[#F0F7FF] flex-shrink-0">↻ Repost</button>
      )}
    </div>
  );
};

const MyFriendliesPage = () => {
  const user = useSelector((s) => s.auth.user);
  const [all, setAll] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("open");
  const [closeFor, setCloseFor] = useState(null);
  const [closing, setClosing] = useState(false);
  const [countries, setCountries] = useState([]);
  const [composerOpen, setComposerOpen] = useState(false);
  const [composerInit, setComposerInit] = useState(null);

  const reload = useCallback(async () => {
    setLoading(true);
    const res = await friendliesService.getMine("");
    if (res.success) setAll(res.data);
    setLoading(false);
  }, []);
  useEffect(() => { reload(); }, [reload]);
  useEffect(() => {
    searchService.getCountries("").then((r) => r.success && setCountries(r.data || [])).catch(() => {});
  }, []);

  const counts = useMemo(() => {
    const c = { open: 0, matched: 0, expired: 0, closed: 0 };
    all.forEach((r) => { c[r.status] = (c[r.status] || 0) + 1; });
    return c;
  }, [all]);
  const shown = useMemo(() => all.filter((r) => r.status === tab), [all, tab]);

  const doClose = async (outcome) => {
    setClosing(true);
    const res = await friendliesService.closeRequest(closeFor.id, outcome);
    setClosing(false);
    if (res.success) { setCloseFor(null); reload(); }
    else toast.error(res.error || "Could not close the request.");
  };

  const repost = async (request) => {
    const res = await friendliesService.getOwnedRequest(request.id);
    const detail = res.success ? res.data : request;
    setComposerInit(prefillFromRequest(detail));
    setComposerOpen(true);
  };
  const openComposer = () => { setComposerInit(null); setComposerOpen(true); };

  return (
    // Platform-standard container (W1-04): no fixed max-width, page scales
    // to 2560+ via the progressive padding ladder.
    <div className="mx-auto px-4 md:px-6 lg:px-7 xl:px-8 min-[1920px]:px-10 min-[2560px]:px-12 py-6">
      <div className="flex items-start justify-between gap-3 mb-4">
        <h1 className="flex items-center gap-2 text-[24px] font-bold text-[#14263D]">
          Our friendly requests
          <ScoutInfoDot id="friendlies-mine" title="Our friendly requests" body="Every request you've posted, with its applications. Start a chat to take it into your normal club-to-club conversation; dismiss quietly to pass. Matched and Closed are your calls; expired ones can be reposted." />
        </h1>
        <button onClick={openComposer} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-[#2283FF] text-white text-[13px] font-semibold hover:bg-[#186FE0] flex-shrink-0">
          <Plus className="w-4 h-4" /> Post a friendly
        </button>
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-4 border-b border-[#E6EAF0] mb-5">
        {TABS.map((t) => (
          <button key={t.value} onClick={() => setTab(t.value)}
            className={`pb-2 -mb-px text-[13px] font-semibold border-b-2 ${tab === t.value ? "border-[#2283FF] text-[#1866CC]" : "border-transparent text-[#8A98AC] hover:text-[#475467]"}`}>
            {t.label} {counts[t.value] > 0 && <span className="ml-0.5 text-[11px]">[{counts[t.value]}]</span>}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="space-y-3">{[0, 1].map((i) => <div key={i} className="h-28 rounded-[12px] bg-[#F0F3F7] animate-pulse" />)}</div>
      ) : shown.length === 0 ? (
        <div className="text-center py-16 rounded-2xl border border-dashed border-[#E4E9F1]">
          <p className="text-[15px] font-semibold text-[#14263D]">{tab === "open" ? "You haven't posted a friendly yet" : `Nothing ${tab} yet`}</p>
          {tab === "open" && (
            <button onClick={openComposer} className="mt-3 text-[13px] font-semibold text-[#2283FF] hover:underline">+ Post a friendly</button>
          )}
        </div>
      ) : (
        <div className="space-y-3">
          {tab === "open"
            ? shown.map((r) => <OpenRequestCard key={r.id} request={r} onClose={setCloseFor} onChanged={reload} />)
            : shown.map((r) => <CalmRequestCard key={r.id} request={r} onRepost={repost} />)}
        </div>
      )}

      <CloseRequestDialog request={closeFor} onChoose={doClose} onCancel={() => setCloseFor(null)} busy={closing} />
      <FriendlyComposer
        open={composerOpen}
        onClose={() => { setComposerOpen(false); reload(); }}
        onPublished={() => {}}
        countries={countries}
        initialState={composerInit}
        viewerClubName={user?.club_name}
      />
    </div>
  );
};

export default MyFriendliesPage;
